import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Card, Form } from "react-bootstrap";
import { ButtonComponent } from "../../components/elements";
import PageLayout from "../../layouts/PageLayout";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Swal from 'sweetalert2';

export default function MyAccountPage() {
    const { t } = useContext(TranslatorContext);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [user, setUser] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    const [formData, setFormData] = useState({
        name: "",
        email: "",
    });
    
    const [passwordData, setPasswordData] = useState({
        password: "",
        password_confirmation: "",
    });
    
    const [avatarPreview, setAvatarPreview] = useState("/images/avatar/01.webp");
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/profile`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            );
            const userData = response.data.user;
            setUser(userData);
            
            setFormData({
                name: userData.name || "",
                email: userData.email || "",
            });
            
            // Set avatar preview
            if (userData.profile_image) {
                setAvatarPreview(`${process.env.REACT_APP_IMG_URL}${userData.profile_image}`);
            } else {
                setAvatarPreview("/images/avatar/01.webp");
            }
        } catch (error) {
            console.error("Error fetching profile:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to load profile',
                confirmButtonColor: '#004d4d'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Check file type
        if (!file.type.startsWith('image/')) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid File',
                text: 'Please upload an image file',
                confirmButtonColor: '#004d4d'
            });
            return;
        }
        
        // Check file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            Swal.fire({
                icon: 'warning',
                title: 'File Too Large',
                text: 'File size must be less than 2MB',
                confirmButtonColor: '#004d4d'
            });
            return;
        }
        
        const formData = new FormData();
        formData.append("profile_image", file);
        formData.append("name", user.name);
        formData.append("email", user.email);
        
        try {
            setUploading(true);
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/profile/update`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            
            // Update avatar preview
            if (response.data.user.profile_image) {
                setAvatarPreview(`${process.env.REACT_APP_IMG_URL}${response.data.user.profile_image}`);
            }
            
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Profile picture updated successfully',
                confirmButtonColor: '#004d4d',
                timer: 2000,
                showConfirmButton: true
            });
            fetchProfile(); // Refresh user data
        } catch (error) {
            console.error("Error uploading avatar:", error);
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                Swal.fire({
                    icon: 'error',
                    title: 'Upload Failed',
                    text: errorMessages[0],
                    confirmButtonColor: '#004d4d'
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Upload Failed',
                    text: 'Failed to upload profile picture',
                    confirmButtonColor: '#004d4d'
                });
            }
        } finally {
            setUploading(false);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        
        try {
            setLoading(true);
            const updateData = {
                name: formData.name,
                email: formData.email,
            };
            
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/profile/update`,
                updateData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );
            
            Swal.fire({
                icon: 'success',
                title: 'Profile Updated!',
                text: response.data.message,
                confirmButtonColor: '#004d4d',
                timer: 2000,
                showConfirmButton: true
            });
            fetchProfile();
        } catch (error) {
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                const errorMessages = Object.values(error.response.data.errors).flat();
                Swal.fire({
                    icon: 'error',
                    title: 'Update Failed',
                    text: errorMessages[0],
                    confirmButtonColor: '#004d4d'
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Update Failed',
                    text: 'Failed to update profile',
                    confirmButtonColor: '#004d4d'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        
        if (passwordData.password !== passwordData.password_confirmation) {
            Swal.fire({
                icon: 'warning',
                title: 'Password Mismatch',
                text: 'Passwords do not match',
                confirmButtonColor: '#004d4d'
            });
            return;
        }
        
        if (passwordData.password.length < 6) {
            Swal.fire({
                icon: 'warning',
                title: 'Password Too Short',
                text: 'Password must be at least 6 characters',
                confirmButtonColor: '#004d4d'
            });
            return;
        }
        
        try {
            setLoading(true);
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/profile/update`,
                {
                    password: passwordData.password,
                    password_confirmation: passwordData.password_confirmation,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );
            
            Swal.fire({
                icon: 'success',
                title: 'Password Updated!',
                text: response.data.message,
                confirmButtonColor: '#004d4d',
                timer: 2000,
                showConfirmButton: true
            });
            setPasswordData({ password: "", password_confirmation: "" });
        } catch (error) {
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                Swal.fire({
                    icon: 'error',
                    title: 'Password Update Failed',
                    text: errorMessages[0],
                    confirmButtonColor: '#004d4d'
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Password Update Failed',
                    text: 'Failed to update password',
                    confirmButtonColor: '#004d4d'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading && !user) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">{t('my_account')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to='#' className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to='#' className="mc-breadcrumb-link">{t('users')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('my_account')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <Card className="p-4 shadow-sm" style={{ borderRadius: "12px" }}>
                        <h4 className="mb-4">{t('Profile Information')}</h4>

                        {/* Profile Picture Section */}
                        <Row className="mb-4">
                            <Col xl={12}>
                                <div className="text-center">
                                    <div className="mb-3">
                                        <img 
                                            src={avatarPreview} 
                                            alt="avatar" 
                                            style={{ 
                                                width: '150px', 
                                                height: '150px', 
                                                objectFit: 'cover', 
                                                borderRadius: '50%',
                                                border: '3px solid #004d4d'
                                            }}
                                        />
                                    </div>
                                    
                                    <div>
                                        <input
                                            type="file"
                                            id="profile_image"
                                            name="profile_image"
                                            accept="image/*"
                                            onChange={handleAvatarUpload}
                                            style={{ display: 'none' }}
                                        />
                                        <label 
                                            htmlFor="profile_image" 
                                            className="btn btn-primary"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {uploading ? t('Uploading...') : t('Upload Profile Picture')}
                                        </label>
                                        
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <Form onSubmit={handleProfileUpdate}>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Label>Full Name {user?.role === "admin" && <span className="text-danger">*</span>}</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        readOnly={user?.role === "doctor"}
                                        disabled={user?.role === "doctor"}
                                        style={user?.role === "doctor" ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                                        placeholder={t('Enter your full name')}
                                        isInvalid={!!errors.name}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name?.[0]}
                                    </Form.Control.Feedback>
                                </Col>

                                <Col md={6}>
                                    <Form.Label>Email Address {user?.role === "admin" && <span className="text-danger">*</span>}</Form.Label>
                                    <Form.Control 
                                        type="email" 
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        readOnly={user?.role === "doctor"}
                                        disabled={user?.role === "doctor"}
                                        style={user?.role === "doctor" ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                                        placeholder={t('Enter your email')}
                                        isInvalid={!!errors.email}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email?.[0]}
                                    </Form.Control.Feedback>
                                </Col>
                            </Row>

                            {user?.role === "admin" && (
                                <div className="text-end">
                                    <ButtonComponent 
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={loading}
                                        text={loading ? t('Saving...') : t('Update Profile')}
                                    />
                                </div>
                            )}
                        </Form>

                        {/* Password Change Section */}
                        <hr className="my-4" />
                        
                        <h4 className="mb-4">{t('Change Password')}</h4>
                        
                        <Form onSubmit={handlePasswordUpdate}>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Label>New Password {user?.role === "doctor" && <span className="text-danger">*</span>}</Form.Label>
                                    <div className="position-relative">
                                        <Form.Control 
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={passwordData.password}
                                            onChange={handlePasswordChange}
                                            placeholder={t('Enter new password')}
                                            isInvalid={!!errors.password}
                                            style={{ paddingRight: '40px' }}
                                        />
                                        <button
                                            type="button"
                                            className="btn position-absolute"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={{ 
                                                border: 'none', 
                                                background: 'transparent', 
                                                padding: '0.375rem 0.75rem', 
                                                color: '#6c757d',
                                                right: '0',
                                                top: '0',
                                                height: '100%',
                                                zIndex: 10,
                                                fontSize: '1.1rem'
                                            }}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.password?.[0]}
                                        </Form.Control.Feedback>
                                    </div>
                                    <small className="text-muted">{t('Password must be at least 6 characters')}</small>
                                </Col>

                                <Col md={6}>
                                    <Form.Label>Confirm Password {user?.role === "doctor" && <span className="text-danger">*</span>}</Form.Label>
                                    <div className="position-relative">
                                        <Form.Control 
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="password_confirmation"
                                            value={passwordData.password_confirmation}
                                            onChange={handlePasswordChange}
                                            placeholder={t('Confirm new password')}
                                            isInvalid={!!errors.password_confirmation}
                                            style={{ paddingRight: '40px' }}
                                        />
                                        <button
                                            type="button"
                                            className="btn position-absolute"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            style={{ 
                                                border: 'none', 
                                                background: 'transparent', 
                                                padding: '0.375rem 0.75rem', 
                                                color: '#6c757d',
                                                right: '0',
                                                top: '0',
                                                height: '100%',
                                                zIndex: 10,
                                                fontSize: '1.1rem'
                                            }}
                                        >
                                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.password_confirmation?.[0]}
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>
                            </Row>

                            <div className="text-end">
                                <ButtonComponent 
                                    type="submit"
                                    className="btn btn-primary px-4"
                                    disabled={loading}
                                    text={loading ? t('Updating...') : t('Change Password')}
                                />
                            </div>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}